// src/products/dataroom/pages/WizardPage.js
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Wand2 } from 'lucide-react';
import SimpleSetupTrigger from '../components/SetupWizard/SimpleSetupTrigger';
import { formatDate } from '../utils/dateUtils';
import { findProjectBySlugOrId } from '../utils/slugUtils';

const WizardPage = ({ projects, onMessage }) => {
  const navigate = useNavigate();
  const { projectId: urlIdentifier } = useParams();
  
  // Find project by slug or ID
  let project = null;
  if (urlIdentifier) {
    const numericId = parseInt(urlIdentifier, 10);
    if (!isNaN(numericId) && numericId > 0) {
      project = projects?.find(p => p.id === numericId);
    } else if (projects && projects.length > 0) {
      project = findProjectBySlugOrId(projects, urlIdentifier);
    }
  }
  
  // Fallback to first project if not found
  if (!project && projects && projects.length > 0) {
    project = projects[0];
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/dealroom')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Wand2 className="w-8 h-8 mr-3 text-blue-600" />
              Dataroom Setup Wizard
            </h1>
            <p className="text-gray-600 mt-1">
              Configure parties, teams, and user access for your dataroom
            </p>
          </div>
        </div>
      </div>

      {/* Project Selection */}
      {project && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">
                  {project.project_name?.charAt(0) || 'P'}
                </span>
              </div>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-blue-900">
                Setting up: {project.project_name}
              </h3>
              <p className="text-sm text-blue-700">
                Project ID: {project.id} • Created: {formatDate(project.created_at)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Wizard Component */}
      <div className="bg-white rounded-lg shadow-lg">
        <SimpleSetupTrigger 
          projectId={project?.id || 1} 
          projectName={project?.project_name || 'Test Project'}
          onSetupComplete={(setupData) => {
            console.log('Setup completed:', setupData);
            onMessage && onMessage('Dataroom setup completed successfully!', 'success');
            // Optionally redirect to the dataroom or show success message
            setTimeout(() => {
              navigate('/dealroom');
            }, 2000);
          }}
        />
      </div>

      {/* Instructions */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Setup Wizard Guide</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <span className="text-blue-600 font-bold text-lg">1</span>
            </div>
            <h4 className="font-medium text-gray-900 mb-2">Create Parties</h4>
            <p className="text-sm text-gray-600">
              Define bidding organizations (e.g., Investment Firm XYZ, Legal Partners)
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <span className="text-purple-600 font-bold text-lg">2</span>
            </div>
            <h4 className="font-medium text-gray-900 mb-2">Setup Teams</h4>
            <p className="text-sm text-gray-600">
              Create teams within each party (Legal, Finance, Tax, etc.)
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <span className="text-green-600 font-bold text-lg">3</span>
            </div>
            <h4 className="font-medium text-gray-900 mb-2">Invite Users</h4>
            <p className="text-sm text-gray-600">
              Add users and assign them to parties and teams
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WizardPage;

